import React, { useContext, useEffect, useState } from 'react';
import type { FC } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import type { MergedI18nComponentProps, MergedI18nModel, MergedI18nModule, MergedI18nOuterProps } from './types';

// `I18nModel.mergeFrom` asserts readiness on its source before reading the resource, so a
// source exposing only the legacy `p$data` surface (e.g. the ads-studio props adapter) throws
// `this.isReady is not a function`. The previous Object.assign-based merge accepted those
// objects, so normalize them here instead of crashing the consuming page. The underlying
// `p$data` still comes from a real model, so the resource merge itself stays intact.
const asMergeSource = (parentI18n: MergedI18nModel): MergedI18nModel => (
  typeof parentI18n?.isReady === 'function'
    ? parentI18n
    : { ...parentI18n, isReady: () => true } as MergedI18nModel
);

const resourceData = (i18n: MergedI18nModel) => i18n?.p$data?.value?.()?.resource?.data;

export const mergeI18n = (target: MergedI18nModel, parentI18n: MergedI18nModel) => {
  if (typeof target?.mergeFrom === 'function') {
    target.mergeFrom(asMergeSource(parentI18n));
  } else {
    // Legacy/demo i18n stand-ins (e.g. the `globalLegacyI18n` mocks demos install on window)
    // expose only `p$data`, not i18n-model's `mergeFrom`. Real models are always on a version
    // that has `mergeFrom`, so this branch never degrades the fallback-flag propagation that
    // the pruned-English resources rely on.
    Object.assign(resourceData(target) || {}, resourceData(parentI18n));
  }

  return target;
};

/* This is to merge the parent and child i18n(App and Feature) */
export const withMergedI18n = <P extends object>(
  Component: React.ComponentType<P & MergedI18nComponentProps>,
  $getI18n: Promise<MergedI18nModule>
): FC<MergedI18nOuterProps<P>> => (props) => {
  const parentI18n = useContext(I18nContext);
  const [i18n, setI18n] = useState<MergedI18nModel | null>(null);

  useEffect(() => {
    $getI18n.then(({ default: childI18n }) => {
      childI18n.ready().then(() => {
        setI18n(mergeI18n(childI18n, parentI18n as MergedI18nModel));
      });
    });
  }, [parentI18n]);

  if (!i18n) return null;

  return (
    <I18nContext.Provider value={i18n}>
      <Component {...props as P} i18n={i18n} />
    </I18nContext.Provider>
  );
};
